/**
 * Currency utilities matching backend rounding logic
 * 
 * Rounding rules:
 * - UYU (Pesos): Round to nearest 10 (1-5 down, 6-9 up)
 * - USD (Dollars): Round to 2 decimal places
 */

/**
 * Round amount based on currency rules
 * @param amount The amount to round
 * @param currency Currency code (UYU or USD)
 * @returns Rounded amount
 */
export function roundByCurrency(amount: number, currency: string = 'UYU'): number {
  if (currency === 'UYU') {
    // Round to nearest 10: 1-5 down, 6-9 up
    const intAmount = Math.round(amount);
    const lastDigit = Math.abs(intAmount) % 10;
    const sign = intAmount >= 0 ? 1 : -1;
    const absAmount = Math.abs(intAmount);

    if (lastDigit <= 5) {
      return sign * (Math.floor(absAmount / 10) * 10);
    } else {
      return sign * (Math.ceil(absAmount / 10) * 10);
    }
  }

  // USD and others: round to 2 decimal places
  return Math.round(amount * 100) / 100;
}

/**
 * Format currency for display
 * @param amount The amount to format
 * @param currency Currency code (UYU or USD)
 * @param applyRounding Whether to apply rounding (only for totals, not line items)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'UYU', applyRounding: boolean = false): string {
  const symbols: Record<string, string> = {
    'UYU': '$',
    'USD': 'US$'
  };

  const symbol = symbols[currency] || currency;
  const displayAmount = applyRounding ? roundByCurrency(amount, currency) : amount;
  const decimals = currency === 'UYU' ? 0 : 2;

  return `${symbol} ${displayAmount.toLocaleString('es-UY', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
}

/**
 * Format currency for totals (with rounding applied)
 * @param amount The amount to format
 * @param currency Currency code (UYU or USD)
 * @returns Formatted currency string with rounding
 */
export function formatCurrencyTotal(amount: number, currency: string = 'UYU'): string {
  return formatCurrency(amount, currency, true);
}

/** Display an already recorded receipt amount without applying cash rounding again. */
export function formatReceiptAmount(amount: number, currency: string = 'UYU'): string {
  const value = Number(amount);
  const symbol = currency === 'UYU' ? '$' : currency === 'USD' ? 'US$' : currency;
  return `${symbol} ${value.toLocaleString('es-UY', {
    minimumFractionDigits: currency === 'UYU' && Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Calculate commission with proper rounding
 * @param amount Base amount
 * @param percentage Commission percentage
 * @param currency Currency code
 * @returns Rounded commission amount
 */
export function calculateCommission(
  amount: number,
  percentage: number,
  currency: string = 'UYU'
): number {
  const commission = amount * (percentage / 100);
  return roundByCurrency(commission, currency);
}

/**
 * Calculate owner payment (rent minus commission) with proper rounding
 * @param rentAmount Rent amount
 * @param commissionPercentage Commission percentage
 * @param currency Currency code
 * @returns Rounded owner payment amount
 */
export function calculateOwnerPayment(
  rentAmount: number,
  commissionPercentage: number,
  currency: string = 'UYU'
): number {
  const commission = rentAmount * (commissionPercentage / 100);
  const ownerPayment = rentAmount - commission;
  return roundByCurrency(ownerPayment, currency);
}

/**
 * Calculate total with proper rounding
 * @param amounts Array of amounts to sum
 * @param currency Currency code
 * @returns Rounded total
 */
export function calculateTotal(amounts: number[], currency: string = 'UYU'): number {
  const total = amounts.reduce((sum, amount) => sum + amount, 0);
  return roundByCurrency(total, currency);
}
